import {action, makeObservable, observable} from "mobx"
import {makePersistable} from "mobx-persist-store"
import {keyBy, merge, noop} from "lodash-es"
import dayjs from "dayjs"
import {t} from "@ui/common/intl"

export type TWidgetPosition = {
  left: number
  top: number
}

export enum ParameterName {
  ENVIRONMENT_DEPTH_BELOW_KEEL = "environment.depth.belowKeel",
  ENVIRONMENT_DEPTH_BELOW_SURFACE = "environment.depth.belowSurface",
  ENVIRONMENT_DEPTH_BELOW_TRANSDUCER = "environment.depth.belowTransducer",
  ENVIRONMENT_DEPTH_SURFACE_TO_TRANSDUCER = "environment.depth.surfaceToTransducer",
  ENVIRONMENT_DEPTH_TRANSDUCER_TO_KEEL = "environment.depth.transducerToKeel",
  ENVIRONMENT_WIND_ANGLE_APPARENT = "environment.wind.angleApparent",
  ENVIRONMENT_WIND_ANGLE_TRUE_WATER = "environment.wind.angleTrueWater",
  ENVIRONMENT_WIND_DIRECTION_MAGNETIC = "environment.wind.directionMagnetic",
  ENVIRONMENT_WIND_DIRECTION_TRUE = "environment.wind.directionTrue",
  ENVIRONMENT_WIND_SPEED_APPARENT = "environment.wind.speedApparent",
  ENVIRONMENT_WIND_SPEED_OVER_GROUND = "environment.wind.speedOverGround",
  ENVIRONMENT_WIND_SPEED_TRUE = "environment.wind.speedTrue",
  NAVIGATION_COURSE_OVER_GROUND_MAGNETIC = "navigation.courseOverGroundMagnetic",
  NAVIGATION_COURSE_OVER_GROUND_TRUE = "navigation.courseOverGroundTrue",
  NAVIGATION_DATETIME = "navigation.datetime",
  NAVIGATION_POSITION = "navigation.position",
  NAVIGATION_RATE_OF_TURN = "navigation.rateOfTurn",
  NAVIGATION_SPEED_OVER_GROUND = "navigation.speedOverGround"
}
type TParameter = {
  id: number
  units: string
}
type TParameters = {
  [key in ParameterName]: TParameter
}
type TStateResponse = {
  name: string
  parameters: TParameters
}

export enum ColumnName {
    PARAM_ID = "param.id",
    SOURCE_DEV = "source.dev",
    SOURCE_PGN = "source.pgn",
    TIMESTAMP = "timestamp",
    VALUE = "value"
}
type TUpdates = {
  columns?: ColumnName[]
  updates?: (any[])[]
}

export type TData = {
  [key: string]: {[key in ColumnName]?: any} & {name: ParameterName} & TParameter
}

// интерфейс для вывода данных на экран и управления ими
export interface IAdapter {
  widgets: string[]
  positions: {[key: string]: TWidgetPosition}
  readonly url: string
  readonly data: TData
  readonly closeWithError: boolean
  readonly isConnected: boolean

  setUrl: (url: string) => void
  toggleWidget: (widget: string) => void
  connect: () => void
  disconnect: () => void
  updateCallbacks: (props: {onData?: () => void, onClose?: (wasClean: boolean) => void}) => void
  getGeoPosition: () => GeolocationPosition | undefined
}

export class Adapter implements IAdapter {
  static readonly MAX_WIDGETS_COUNT: number = 3;

  widgets: string[] = []
  positions: {[key: string]: TWidgetPosition} = {}
  url: string = process.env.TEST_BOAT_SOCKET_URL || ""
  isConnected: boolean = false
  closeWithError: boolean = false
  data: TData = {}
  #ws?: WebSocket

  #onData: () => void = noop
  #onClose: (wasClean: boolean) => void = noop

  constructor() {
    makeObservable(this, {
			widgets: observable,
      positions: observable,
      data: observable,
      isConnected: observable,
      closeWithError: observable,
      url: observable,

      setUrl: action,
			toggleWidget: action,
			connect: action,
      disconnect: action
    })

		makePersistable(this, {
			name: 'adapter',
			properties: ["positions"],
			storage: window.localStorage
		})
  }

  static parseValue (data?: TData[keyof TData]): string[] {
    if (!data?.value) {
      return ["-"]
    }
    switch (data.name) {
      case ParameterName.NAVIGATION_POSITION:
        const {lat, lon} = data.value as {lat: number, lon: number}
        return [`${lat.toFixed(6)}, ${lon.toFixed(6)}`]
      case ParameterName.NAVIGATION_DATETIME:
        return [dayjs.unix(data.value).format('DD.MM.YYYY HH:mm')]
    }
    let val = data.value
    if (Number(data.value) === data.value && data.value % 1 !== 0) {
      val = data.value.toFixed(2)
    }
    return [val.toString(), `${t(`adapter.units.${data.units.toLowerCase()}.title`)}`]
  }

  getGeoPosition() {
    const positionEntry = Object.entries(this.data)
      .find(([key, value]) => value.name === ParameterName.NAVIGATION_POSITION)

    const speedEntry = Object.entries(this.data)
      .find(([key, value]) => value.name === ParameterName.NAVIGATION_SPEED_OVER_GROUND)

    if (positionEntry && speedEntry) {
      return {
        coords: {
          accuracy: 0,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          latitude: positionEntry[1].value.lat,
          longitude: positionEntry[1].value.lon,
          speed: speedEntry[1].value,
        },
        timestamp: Date.now(),
      }
    }
  }

  setUrl(url: string) {
    this.url = url
  }

  toggleWidget(widget: string) {
    if (this.widgets.includes(widget)) {
      this.widgets = this.widgets.filter(w => w !== widget)
    } else if (this.widgets.length < Adapter.MAX_WIDGETS_COUNT) {
      this.widgets = [...this.widgets, widget]
    }
  }

  connect() {
    this.closeWithError = false

    this.#ws = new WebSocket(`${this.url}`)

    this.#ws.onopen = () => this.isConnected = true

    this.#ws.onclose = (e) => {
      this.closeWithError = !e.wasClean
      this.#onClose(e.wasClean)
      this.widgets = []
      this.data = {}
      this.#onData = noop
      this.#onClose = noop
      this.isConnected = false
    }

    this.#ws.onmessage = (msg: MessageEvent<string>) => {
      const msgState = JSON.parse(msg.data)?.state as unknown as TStateResponse
      this.data = keyBy(
        Object.entries(msgState.parameters)
          .map(([key, value]) => ({...value, name: key as ParameterName})),
        "id"
      )

      if (this.#ws) {

        this.#ws.onmessage = (msg: MessageEvent<string>) => {
          const {updates = [], columns = []} = (JSON.parse(msg.data) || {}) as unknown as TUpdates
          const data: {[key: string]: {[key in ColumnName]?: any}} = keyBy(
            updates
              .map(values => Object.fromEntries(columns.map((key, index) => [key, values[index]]))),
            ColumnName.PARAM_ID
          )
          this.data = merge(this.data, data)
          this.#onData()
        }

        const subscribe = [
          {
            params: Object.entries(this.data).map(([key]) => Number(key)),
            policy: "fixed",
            period: 1000,
            minPeriod: 200
          }
        ]

        this.#ws.send(JSON.stringify({subscribe}))
      }
    }
  }

  updateCallbacks({onData = noop, onClose = noop}: {
    onData?: () => void,
    onClose?: (error: any) => void,
  }) {
    this.#onData = onData
    this.#onClose = onClose
  }

  disconnect() {
    if (this.isConnected) {
      this.#ws?.close()
		}
  }

}
